import type { CreateTool, ToolModel, UpdateTool } from '@/types/tool.types';
import type { ToolSystem } from '@/types/toolSystem.types';
import { apiClient } from './client';

/**
 * Server-side list filter (matches backend ToolListFilter). null = all, [] = none, [...] = only these.
 *
 * The holder and tool-number filters are gone with the tool-database redesign: both moved
 * onto the assembly, so they are only filterable through `GET /assemblies`.
 */
export interface ToolsListFilterParams {
  tool_types?: string[] | null;
  active?: boolean | null;
  order?: 'asc' | 'desc';
  /** Only the tools of this catalog; names are only unique within one. */
  // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
  tool_system?: ToolSystem | null;
}

export interface ToolsListParams extends ToolsListFilterParams {
  page?: number;
  size?: number;
}

function appendListFilterParams(searchParams: URLSearchParams, params?: ToolsListFilterParams | null): void {
  if (!params) return;
  if (params.tool_types != null) {
    params.tool_types.forEach((tt) => searchParams.append('tool_types', tt));
  }
  if (params.active != null) searchParams.set('active', String(params.active));
  if (params.order != null) searchParams.set('order', params.order);
  if (params.tool_system != null) searchParams.set('tool_system', params.tool_system);
}

export const toolsApi = {
  /** POST /tools - create a new tool. Returns the new tool's id. */
  create: async (body: CreateTool): Promise<{ id: string }> => {
    const { data } = await apiClient.post<{ id: string }>('/tools', body);
    return data;
  },

  /** GET /tools - list tools with optional pagination and filter (tool_types, active, order). */
  getAll: async (params?: ToolsListParams): Promise<ToolModel[]> => {
    const searchParams = new URLSearchParams();
    appendListFilterParams(searchParams, params);
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.size != null) searchParams.set('size', String(params.size));
    const query = searchParams.toString();
    const url = query ? `/tools?${query}` : '/tools';
    const { data } = await apiClient.get<ToolModel[]>(url);
    return data;
  },

  /** GET /tools/count - total count with same filter (tool_types, active). */
  getCount: async (params?: ToolsListFilterParams | null): Promise<number> => {
    const searchParams = new URLSearchParams();
    appendListFilterParams(searchParams, params);
    const query = searchParams.toString();
    const url = query ? `/tools/count?${query}` : '/tools/count';
    const { data } = await apiClient.get<{ total: number }>(url);
    return data.total;
  },

  /** GET /tools/{tool_id} - fetch a single tool with nested holder. */
  getById: async (toolId: string): Promise<ToolModel> => {
    const { data } = await apiClient.get<ToolModel>(`/tools/${toolId}`);
    return data;
  },

  /** PUT /tools/{tool_id} - update a tool by id. */
  update: async (toolId: string, body: UpdateTool): Promise<ToolModel> => {
    const { data } = await apiClient.put<ToolModel>(`/tools/${toolId}`, body);
    return data;
  },

  /** DELETE /tools/{tool_id} - delete a tool by id. */
  delete: async (toolId: string): Promise<void> => {
    await apiClient.delete(`/tools/${toolId}`);
  },
};
